import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useRouteMatch } from 'react-router';
import {
  Column,
  RowInfo,
  SortingRule,
} from 'react-table';
import { Grid, Box } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import clsx from 'clsx';
import FiltersBar from '../filter-bar/FiltersBar';
import SearchBar from './SearchBar';
import { updateQuery, getQueryData } from '../../utility/helpers';
import {
  Filter,
  ItemList,
  ListParams,
  SortDir,
  FilterMap,
} from '../../types/main';
import { Item } from '../../types/items';
import { empty } from '../../utility/filters';
import { AddNewButton } from './AddNewButton';
import MaterialTable from '../material-table/MaterialTable';
import SavedSearchService, { SavedSearchPages } from '../../services/SavedSearchService';
import { setHoveredItemId } from '../../store/modules/lists/actions';
import { getActualFilters } from './util';
import { useStyles } from './styles';
import { makeFilterMap } from '../filter-bar/utils';
import { selectCurrentUser } from '../../store/modules/users/selectors';
import FilterMenus from '../filter-bar/FilterMenu/FilterMenus';

const makeSortingRule = (key?: string, dir?: SortDir) => (key ? {
  id: key,
  desc: dir === 'DESC',
} : null);

type Props<T extends Item = Item> = {
  columns: Array<Column & { id: string }>;
  loading: boolean;
  data: ItemList<T> | null;
  fetch: (listParams: ListParams) => void;
  dirty: boolean;
  setDirty: (state: boolean) => void;
  addTitle?: string;
  addButtonHandler?: () => void;
  requiredFilters?: Filter[];
  filterMap?: FilterMap[];
  adminColumns?: string[];
  disabledCreate?: boolean;
  hideSearch?: boolean;
  hideFilters?: boolean;
  hideAddButton?: boolean;
  viewEnable?: boolean;
};

const ListTable = ({
  columns,
  loading,
  fetch,
  data,
  dirty,
  setDirty,
  addTitle,
  addButtonHandler,
  requiredFilters,
  filterMap,
  adminColumns,
  disabledCreate = false,
  hideSearch = false,
  hideFilters = false,
  viewEnable = true,
  hideAddButton = false,
}: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const canAdd = data && data.permissions.can_add && !hideAddButton;
  const { data: user } = useSelector(selectCurrentUser);
  const items = !loading && data ? data.items : [];
  const queryData = getQueryData();
  // urs & paths
  const { url } = useRouteMatch();
  const pathSegments = url.split('/');
  // table - pagination
  const actualPagination = data ? data.pagination : queryData!.pagination;
  const pages = Math.ceil(actualPagination.totalItems / actualPagination.pageSize);
  const [page, setPage] = useState(actualPagination.page);
  const [pageSize, setPageSize] = useState(actualPagination.pageSize);
  // table - sort
  const actualSorting = data ? data.sorting : queryData!.sorting;
  const [sorting, setSorting] = useState<SortingRule | null>(makeSortingRule(actualSorting.key, actualSorting.dir));
  // table - filters
  const actualFilters = getActualFilters(requiredFilters, data);
  const commonFilters = actualFilters.active.filter((f) => f.key !== 'search');
  const [activeFilters, setActiveFilters] = useState<Filter[]>(commonFilters);
  const baseLineFiltersKeys = useMemo(() => (user ? makeFilterMap(user, filterMap)
    .map((f) => f.key) : []), [user, filterMap]);
  // table - search
  const searchFilterValue = actualFilters.active.find((f) => f.key === 'search')?.value.toString() || '';
  const [search, setSearch] = useState<string>(searchFilterValue);
  const [searchInputRightPosition, setSearchInputRightPosition] = useState(true);
  const savedSearchPage = pathSegments[pathSegments.length - 1] as SavedSearchPages;

  const tableColumns = useMemo(() => {
    const columnsMap = columns.map(({ id, accessor = id, ...col }) => ({
      id,
      accessor,
      sortable: actualSorting.available.includes(id),
      ...col,
    }));

    if (adminColumns && activeFilters.find((active) => active.key === 'admin') === undefined) {
      return columnsMap.filter((column) => adminColumns.find((admin) => admin === column.id) === undefined);
    }

    return columnsMap;
  }, [actualSorting.available, columns, activeFilters, adminColumns]);

  /** callbacks */
  const changeSorting = useCallback(([sortingValue]) => {
    setSorting(sortingValue);
    setPage(0);
    setDirty(true);
  }, [setSorting, setPage, setDirty]);

  const changePageSize = useCallback((pageSizeValue) => {
    setPageSize(pageSizeValue);
    setPage(0);
    setDirty(true);
  }, [setPageSize, setPage, setDirty]);

  const changePage = useCallback((pageValue) => {
    setPage(pageValue);
    setDirty(true);
  }, [setPage, setDirty]);

  // change base filter from filter line (save additional filters)
  const changeActiveFilters = useCallback((activeFiltersValue: Filter[]) => {
    if (requiredFilters) {
      requiredFilters.forEach((filter) => {
        const activeFilter = activeFiltersValue.find((actFilter) => actFilter.key === filter.key);
        if (activeFilter === undefined) {
          activeFiltersValue.push(filter);
        }
      });
    }

    const additionalFilters = activeFilters.filter((f) => !baseLineFiltersKeys.includes(f.key));
    const newFilters = [
      ...additionalFilters,
      ...activeFiltersValue,
    ];

    setActiveFilters(newFilters);
    setPage(0);
    setDirty(true);
  }, [setActiveFilters, setPage, setDirty, requiredFilters, baseLineFiltersKeys, activeFilters]);

  const changeSearch = useCallback((searchValue) => {
    setSearch(searchValue);
    setPage(0);
    setDirty(true);
  }, [setSearch, setPage, setDirty]);

  const updateList = useCallback((filters) => {
    const appliedFilters = filters.length ? Object.fromEntries(filters) : [];

    if (search) {
      appliedFilters.search = search;
    }
    const params = Object.fromEntries(
      [
        ['page', page],
        ['page_size', pageSize],
        ['sort_key', sorting && sorting.id],
        ['sort_dir', sorting && (sorting.desc ? 'DESC' : 'ASC')],
        ['filter', appliedFilters],
        ['search', search],
      ].filter(([, val]) => empty(val)),
    ) as ListParams;

    fetch(params);
    updateQuery(params);
  }, [page, pageSize, sorting, fetch, search]);

  const addNewHandler = useCallback(() => {
    if (search && !items.length) {
      SavedSearchService.setValue(savedSearchPage, search);
    }
    if (addButtonHandler) {
      addButtonHandler();
    } else {
      dispatch(push(`${url}${((url.charAt(url.length - 1)) === '/' ? '' : '/')}new`));
    }
  }, [url, dispatch, addButtonHandler, search, items, savedSearchPage]);

  const addKeyPressHandler = useCallback(() => {
    if (search && !items.length) {
      SavedSearchService.setValue(savedSearchPage, search);
      dispatch(push(`${url}${((url.charAt(url.length - 1)) === '/' ? '' : '/')}new`));
    }
  }, [url, dispatch, items, search, savedSearchPage]);

  const trPropsMapper = useCallback((_: unknown, rowInfo?: RowInfo,) => ({
    onClick: () => {
      if (rowInfo?.original?.permissions.can_view && viewEnable) {
        dispatch(push(`${url}${((url.charAt(url.length - 1)) === '/' ? '' : '/')}${rowInfo.original.id}`));
      }
    },
    onMouseEnter: () => {
      if (rowInfo?.original?.id) {
        dispatch(setHoveredItemId(rowInfo.original.id));
      }
    },
    onMouseLeave: () => {
      dispatch(setHoveredItemId(0));
    },
    className: classes.row,
  }), [dispatch, url, classes, viewEnable]);

  const theadPropsMapper = useCallback(() => ({
    className: classes.head,
  }), [classes]);

  const thPropsMapper = useCallback(() => ({
    className: classes.headCell,
  }), [classes]);

  /** effects */
  // Save last hovered row id for hotkeys
  useEffect(() => {
    dispatch(setHoveredItemId(0));
  }, [dispatch]);

  // useEffect(() => setPage(actualPagination.page), [setPage, actualPagination.page]);

  // useEffect(() => setPageSize(actualPagination.pageSize), [setPageSize, actualPagination.pageSize]);

  // useEffect(() => setSearch(searchFilterValue), [setSearch, searchFilterValue]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setActiveFilters(commonFilters), [setActiveFilters, JSON.stringify(commonFilters)]);

  useEffect(() => setSorting(makeSortingRule(actualSorting.key, actualSorting.dir)), [
    setSorting,
    actualSorting.key,
    actualSorting.dir,
  ]);

  // Main effect for call table update after changing dirty
  useEffect(() => {
    const checkFilter = () => {
      if (requiredFilters) {
        return requiredFilters.every((item) => !(item.value === undefined || item.value === null));
      }
      return true;
    };

    if (dirty && checkFilter()) {
      const filters = [
        ...activeFilters.map(({ key, value }) => [key, value]),
      ].filter(([, val]) => empty(val) && val !== '');

      updateList(filters);
    }
  }, [dirty, activeFilters, fetch, page, pageSize, requiredFilters, sorting, updateList]);

  useEffect(() => {
    if (!loading) {
      setSearchInputRightPosition(Boolean(search && !items.length && !loading));
    }
  }, [loading, setSearchInputRightPosition, items, search]);

  return (
    <Grid container>
      <Grid
        className={clsx(classes.searchContainer, {
          [classes.reversedDirection]: searchInputRightPosition,
          [classes.hidden]: hideSearch,
        })}
      >
        <Box className={classes.searchBar}>
          <SearchBar addHandler={addKeyPressHandler} value={search} onChange={changeSearch} />
        </Box>
        {canAdd && (
          <Box className={classes.addNew}>
            <AddNewButton
              title={addTitle}
              onAdd={addNewHandler}
              isRightSide={!searchInputRightPosition}
              disable={loading || disabledCreate}
            />
          </Box>
        )}
      </Grid>
      <Grid
        item
        xs={12}
        className={clsx(classes.filter, {
          [classes.hidden]: (hideFilters || !actualFilters.available.length),
        })}
      >
        <FiltersBar
          available={actualFilters.available}
          active={activeFilters}
          onChange={changeActiveFilters}
          filterMap={filterMap}
          className={classes.wideFilterBar}
        />
        <FilterMenus
          onApply={updateList}
          currentFilters={activeFilters}
        />
      </Grid>
      <Grid item xs={12}>
        <MaterialTable
          className={classes.table}
          manual
          loading={loading}
          columns={tableColumns}
          data={items}
          page={page}
          pages={pages}
          count={actualPagination.totalItems}
          pageSize={pageSize}
          sorted={[sorting].filter(empty)}
          showPagination={pages > 1}
          onPageChange={changePage}
          onPageSizeChange={changePageSize}
          onSortedChange={changeSorting}
          getTrProps={trPropsMapper}
          getTheadProps={theadPropsMapper}
          getTheadThProps={thPropsMapper}
        />
      </Grid>
    </Grid>
  );
};

export default ListTable;
